/**
 * BroadcastComposerPanel — founder + CS UI to send an announcement to
 * one or more audiences across in-app, push, and email channels.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  useSendBroadcast,
  useSentBroadcasts,
} from "@/lib/broadcasts/useBroadcasts";
import type { BroadcastAudienceKind } from "@/lib/broadcasts/types";

const TIERS = ["tier_signal", "tier_pulse", "tier_surge", "tier_spectrum", "tier_attuned"];
const APPS = [
  "fryqnc", "pulsynq", "mindlynq",
  "ablelynq", "deafwav", "fullynq", "jadesynq", "openlynq", "spiritsynq",
];
const ROLES = ["mentor", "host", "cs_rep", "moderator", "admin"];

export const BroadcastComposerPanel = () => {
  const { toast } = useToast();
  const send = useSendBroadcast();
  const sent = useSentBroadcasts();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [audienceKind, setAudienceKind] = useState<BroadcastAudienceKind>("all");
  const [audienceValue, setAudienceValue] = useState<string>("");
  const [inApp, setInApp] = useState(true);
  const [push, setPush] = useState(false);
  const [email, setEmail] = useState(false);

  const submit = async () => {
    if (!title.trim() || !body.trim()) {
      toast({ title: "Title and body are required", variant: "destructive" });
      return;
    }
    if (audienceKind !== "all" && !audienceValue) {
      toast({ title: "Pick an audience target", variant: "destructive" });
      return;
    }
    if (!inApp && !push && !email) {
      toast({ title: "Pick at least one channel", variant: "destructive" });
      return;
    }
    try {
      const res = await send.mutateAsync({
        title: title.trim(),
        body: body.trim(),
        cta_label: ctaLabel.trim() || undefined,
        cta_url: ctaUrl.trim() || undefined,
        audience_kind: audienceKind,
        audience_value: audienceKind === "all" ? undefined : audienceValue,
        channels: { in_app: inApp, push, email },
      });
      toast({
        title: "Broadcast sent",
        description: `${res.recipients} recipients · ${res.delivered} delivered`,
      });
      setTitle("");
      setBody("");
      setCtaLabel("");
      setCtaUrl("");
    } catch (e) {
      toast({
        title: "Send failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };

  const audienceOptions = (() => {
    if (audienceKind === "app") return APPS;
    if (audienceKind === "tier") return TIERS;
    if (audienceKind === "role") return ROLES;
    return [];
  })();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Compose broadcast</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="A friendly headline (max 200 chars)"
              maxLength={200}
            />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="The full announcement…"
              maxLength={4000}
            />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>CTA label (optional)</Label>
              <Input
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                placeholder="Read the update"
              />
            </div>
            <div>
              <Label>CTA URL (optional)</Label>
              <Input
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                placeholder="https://…"
              />
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Audience</Label>
              <Select
                value={audienceKind}
                onValueChange={(v) => {
                  setAudienceKind(v as BroadcastAudienceKind);
                  setAudienceValue("");
                }}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone (all apps)</SelectItem>
                  <SelectItem value="app">By app</SelectItem>
                  <SelectItem value="tier">By tier</SelectItem>
                  <SelectItem value="role">By role</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {audienceOptions.length > 0 && (
              <div>
                <Label>Target</Label>
                <Select value={audienceValue} onValueChange={setAudienceValue}>
                  <SelectTrigger><SelectValue placeholder="Pick…" /></SelectTrigger>
                  <SelectContent>
                    {audienceOptions.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <Label>Channels</Label>
            <div className="mt-2 flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={inApp} onCheckedChange={(v) => setInApp(!!v)} />
                In-app banner + inbox
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={push} onCheckedChange={(v) => setPush(!!v)} />
                Push notification
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={email} onCheckedChange={(v) => setEmail(!!v)} />
                Email (Resend)
              </label>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={submit} disabled={send.isPending}>
              {send.isPending ? "Sending…" : "Send broadcast"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent broadcasts</CardTitle>
        </CardHeader>
        <CardContent>
          {sent.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (sent.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No broadcasts sent yet.</p>
          ) : (
            <ul className="space-y-2">
              {sent.data!.map((b) => (
                <li
                  key={b.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{b.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {b.audience_kind}
                      {b.audience_value ? `:${b.audience_value}` : ""} ·{" "}
                      {b.recipient_count} recipients ·{" "}
                      {new Date(b.created_at).toLocaleString()}
                    </p>
                  </div>
                  <Badge variant={b.status === "sent" ? "secondary" : "destructive"}>
                    {b.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
